import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView, useScroll, useTransform, animate } from "framer-motion";
import {
  Check, CheckCircle2, ArrowRight, ChevronLeft, ChevronRight,
  ChevronDown, Monitor, Package, CreditCard, Truck, Megaphone, BarChart3, Smartphone, Settings, Store,
  Palette, Mail, PhoneCall, MessageCircle, Search, Bell, LayoutDashboard, Users, Tag, Sparkles,
  Headphones, Apple, Carrot, ShoppingBasket, Shirt, Laptop, Sofa, Lamp, Flower2, Brush,
  Gem, Watch, Scissors, Paintbrush, Coffee, Pizza, UtensilsCrossed, Dumbbell, Trophy, Footprints,
  Armchair, Droplets, Glasses, Wheat, Cherry, Plus, Menu, X,
} from "lucide-react";
import DashboardShowcase from "@/components/landing/DashboardShowcase";
import api from "@/lib/api";
import "./landing.css";

const G = "#2563eb";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};
const fromLeft = { hidden: { opacity: 0, x: -48 }, show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const fromRight = { hidden: { opacity: 0, x: 48 }, show: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

function Reveal({ children, custom = 0, variants = reveal, className = "", ...rest }) {
  return (
    <motion.div variants={variants} custom={custom} initial="hidden" whileInView="show"
      viewport={{ once: true, margin: "-80px" }} className={className} {...rest}>
      {children}
    </motion.div>
  );
}

function CountUp({ to, prefix = "", suffix = "", decimals = 0, format }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, to, { duration: 1.6, ease: "easeOut", onUpdate: (x) => setV(x) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{prefix}{format ? format(v) : v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

function Brand({ dark = false }) {
  return (
    <img src={dark ? "/logo-dark.png" : "/logo-light.png"} alt="sell24x7.com"
      className={dark ? "h-12 w-auto" : "h-10 w-auto"} data-testid="brand-logo" />
  );
}

/* ---------- Mockups (pure CSS/SVG) ---------- */
function ProductTile({ c1, c2, label, price }) {
  return (
    <div className="rounded-md bg-white border border-slate-100 p-1">
      <div className="h-9 rounded-sm" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }} />
      <div className="text-[5px] text-slate-500 mt-0.5 truncate">{label}</div>
      <div className="text-[5px] font-bold text-neutral-900">{price}</div>
    </div>
  );
}

function LaptopMock() {
  return (
    <div className="relative w-full">
      <div className="rounded-t-2xl bg-neutral-900 p-2 pb-0 shadow-2xl">
        <div className="rounded-t-lg bg-white overflow-hidden aspect-[16/10]">
          <div className="flex items-center justify-between px-3 py-1 border-b border-slate-100">
            <span className="text-[6px] font-bold text-slate-700">My Store</span>
            <div className="flex gap-2 text-[5px] text-slate-400"><span>Home</span><span>Shop</span><span>About</span><span>Contact</span></div>
          </div>
          <div className="grid grid-cols-2 gap-1 p-2 bg-amber-50">
            <div className="pt-3 pl-2">
              <div className="text-[5px] text-blue-700 font-semibold">New Collection</div>
              <div className="font-display font-extrabold text-[11px] leading-tight text-neutral-900 mt-0.5">Stylish<br />Home Decor</div>
              <div className="mt-1.5 inline-block text-[5px] px-1.5 py-0.5 rounded bg-neutral-900 text-white">Shop Now</div>
            </div>
            <div className="h-16 rounded-md bg-gradient-to-br from-orange-200 via-amber-300 to-blue-300 relative">
              <div className="absolute bottom-1 left-2 w-5 h-8 rounded-t-full bg-blue-700/70" />
              <div className="absolute bottom-1 right-3 w-8 h-5 rounded bg-orange-500/70" />
            </div>
          </div>
          <div className="px-2 pt-1 text-[6px] font-bold text-slate-700">Best Sellers</div>
          <div className="grid grid-cols-4 gap-1 p-2 pt-1">
            <ProductTile c1="#fde68a" c2="#f97316" label="Table Lamp" price="$49" />
            <ProductTile c1="#bbf7d0" c2="#16a34a" label="Plant Pot" price="$19" />
            <ProductTile c1="#fecaca" c2="#ef4444" label="Cushion" price="$25" />
            <ProductTile c1="#bfdbfe" c2="#3b82f6" label="Vase" price="$32" />
          </div>
        </div>
      </div>
      <div className="h-2.5 bg-gradient-to-b from-slate-300 to-slate-400 rounded-b-xl mx-[-4%]" />
    </div>
  );
}

function TabletMock() {
  return (
    <div className="w-full rounded-xl bg-neutral-900 p-1.5 shadow-2xl">
      <div className="rounded-md bg-white overflow-hidden aspect-[3/4] p-2">
        <div className="text-[6px] font-bold text-slate-700 text-center">My Store</div>
        <div className="mt-1 h-16 rounded bg-gradient-to-br from-rose-200 to-orange-300" />
        <div className="text-[6px] font-bold mt-1 text-slate-700">Trendy Fashion</div>
        <div className="grid grid-cols-2 gap-1 mt-1">
          <div className="h-10 rounded bg-gradient-to-br from-sky-200 to-indigo-300" />
          <div className="h-10 rounded bg-gradient-to-br from-blue-200 to-blue-400" />
        </div>
      </div>
    </div>
  );
}

function PhoneMock({ variant = "home", className = "" }) {
  return (
    <div className={`rounded-[22px] bg-black p-1.5 shadow-2xl ${className}`}>
      <div className="rounded-[17px] bg-white overflow-hidden aspect-[9/19] p-2 relative">
        <div className="mx-auto w-8 h-1 rounded-full bg-slate-200 mb-2" />
        {variant === "home" && (<>
          <div className="text-[7px] font-bold text-slate-700 flex justify-between"><span>My Store</span><Search size={8} /></div>
          <div className="mt-1.5 h-20 rounded-lg bg-gradient-to-br from-amber-200 to-orange-400 p-1.5">
            <div className="text-[6px] font-bold text-white">Summer Collection</div>
            <div className="mt-1 inline-block text-[5px] bg-white rounded px-1 py-0.5 text-slate-700">Shop Now</div>
          </div>
          <div className="text-[7px] font-bold mt-1.5 text-slate-700">Best Sellers</div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <div className="h-14 rounded bg-gradient-to-br from-stone-200 to-stone-400" />
            <div className="h-14 rounded bg-gradient-to-br from-slate-300 to-slate-500" />
          </div>
        </>)}
        {variant === "product" && (<>
          <div className="h-24 rounded-lg bg-gradient-to-br from-slate-100 to-slate-300 flex items-center justify-center">
            <div className="w-16 h-8 rounded-full bg-slate-700 rotate-[-12deg]" />
          </div>
          <div className="text-[7px] font-bold mt-1.5 text-neutral-900">Classic Sneakers</div>
          <div className="text-[8px] font-extrabold text-blue-700">$49</div>
          <div className="flex gap-1 mt-1">{["#1e3a8a", "#dc2626", "#e5e7eb"].map((c) => <span key={c} className="w-2.5 h-2.5 rounded-full border border-slate-200" style={{ background: c }} />)}</div>
          <div className="absolute inset-x-2 bottom-2 text-center text-[7px] font-bold text-white bg-blue-600 rounded-md py-1.5">Add to cart</div>
        </>)}
        {variant === "orders" && (<>
          <div className="text-[7px] font-bold text-slate-700">My Orders</div>
          {[["Running Shoes", "Delivered", "#16a34a"], ["Smart Watch", "Processing", "#f59e0b"], ["Backpack", "Shipped", "#3b82f6"]].map(([n, s, c]) => (
            <div key={n} className="mt-1.5 flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
              <div className="w-6 h-6 rounded bg-slate-200" />
              <div className="flex-1"><div className="text-[6px] font-semibold text-slate-700">{n}</div><div className="text-[5px] font-bold" style={{ color: c }}>{s}</div></div>
            </div>
          ))}
        </>)}
      </div>
    </div>
  );
}

/* Support illustration: a chat window with an agent, plus the three ways to reach us. */
function SupportArt() {
  const float = (d = 0) => ({ animate: { y: [0, -10, 0] }, transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: d } });
  return (
    <div className="relative mx-auto w-full max-w-[460px] h-[330px] select-none" aria-hidden data-testid="support-art">
      <div className="absolute inset-6 rounded-full bg-white/10" />
      <div className="absolute inset-x-12 inset-y-14 rounded-full border-2 border-dashed border-white/25" />
      <div className="absolute left-1/2 top-[44px] -translate-x-1/2 w-[290px] max-w-[88%]"><motion.div {...float(0)} className="w-full rounded-2xl bg-white shadow-2xl shadow-blue-950/40 overflow-hidden">
        <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
          <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Headphones size={16} /></span>
          <div className="leading-tight"><div className="text-[12px] font-bold">Sell24x7 Support</div><div className="text-[10px] flex items-center gap-1 opacity-90"><i className="w-1.5 h-1.5 rounded-full bg-green-300 not-italic" /> Online now</div></div>
        </div>
        <div className="p-3.5 space-y-2 text-[11px]">
          <div className="max-w-[78%] rounded-xl rounded-tl-sm bg-slate-100 text-slate-700 px-3 py-2">Hi! How can we help your store today?</div>
          <div className="max-w-[78%] ml-auto rounded-xl rounded-tr-sm bg-blue-600 text-white px-3 py-2">I need help setting up my domain.</div>
          <div className="max-w-[84%] rounded-xl rounded-tl-sm bg-slate-100 text-slate-700 px-3 py-2">Sure — it takes two minutes. Let&apos;s do it together.</div>
          <div className="flex gap-1 pl-1">{[0, 1, 2].map((i) => <motion.i key={i} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }} className="w-1.5 h-1.5 rounded-full bg-slate-400" />)}</div>
        </div>
      </motion.div>
      </div>
      <motion.div {...float(0.6)} className="absolute right-2 top-2 w-[72px] h-[72px] rounded-full bg-gradient-to-br from-orange-300 to-orange-500 border-4 border-white/80 shadow-xl overflow-hidden">
        <svg viewBox="0 0 72 72" className="w-full h-full"><circle cx="36" cy="28" r="12" fill="#fde4cf" /><path d="M10 72c2-18 14-26 26-26s24 8 26 26z" fill="#1e3a8a" /><path d="M22 27a14 14 0 0 1 28 0" fill="none" stroke="#0f172a" strokeWidth="3" /><rect x="18" y="26" width="6" height="11" rx="3" fill="#0f172a" /><rect x="48" y="26" width="6" height="11" rx="3" fill="#0f172a" /><path d="M24 37q-2 8 10 8" fill="none" stroke="#0f172a" strokeWidth="2" /></svg>
      </motion.div>
      <motion.span {...float(0.3)} className="absolute left-0 top-24 w-12 h-12 rounded-2xl bg-white shadow-xl flex items-center justify-center text-blue-600"><Mail size={22} /></motion.span>
      <motion.span {...float(0.9)} className="absolute left-4 bottom-3 w-12 h-12 rounded-2xl bg-orange-400 shadow-xl flex items-center justify-center text-white"><PhoneCall size={21} /></motion.span>
      <motion.span {...float(1.2)} className="absolute right-0 bottom-1 w-12 h-12 rounded-2xl bg-green-500 shadow-xl flex items-center justify-center text-white"><MessageCircle size={22} /></motion.span>
      <motion.span {...float(0.2)} className="absolute right-[88px] bottom-0 px-3 py-1.5 rounded-full bg-white shadow-lg text-[11px] font-extrabold text-blue-700">24 × 7</motion.span>
    </div>
  );
}

/* ---------- Data ---------- */
const FEATURES = [
  [Monitor, "Beautiful Store Themes"], [Package, "Easy Product Management"], [CreditCard, "Multiple Payment Options"],
  [Truck, "Delivery & Shipping Integration"], [Megaphone, "Marketing Tools & Promotions"], [BarChart3, "Analytics & Reports"],
  [Smartphone, "Mobile App (Android & iOS)"], [Settings, "Complete Store Management"],
];
const INTEGRATIONS = [
  ["PayPal", "#1e3a8a", "italic"], ["stripe", "#635bff", ""], ["Razorpay", "#0b3d91", "italic"], ["G Pay", "#374151", ""], ["PhonePe", "#5f259f", ""], ["aws", "#111827", ""],
  ["DHL", "#d40511", "italic"], ["FedEx", "#4d148c", ""], ["shiprocket", "#5b3df5", ""], ["Google Analytics", "#e37400", ""], ["Meta", "#0866ff", ""], ["API", "#475569", ""],
];
// [name, hero icon, three product icons, tile background, accent]
const CATEGORIES = [
  ["Grocery & Food", ShoppingBasket, [Apple, Carrot, Wheat], "#bbf7d0", "#16a34a"],
  ["Fashion & Apparel", Shirt, [Footprints, Glasses, Shirt], "#fbcfe8", "#db2777"],
  ["Electronics & Gadgets", Smartphone, [Headphones, Laptop, Watch], "#bfdbfe", "#2563eb"],
  ["Home & Living", Sofa, [Lamp, Armchair, Flower2], "#fde68a", "#d97706"],
  ["Beauty & Personal Care", Sparkles, [Droplets, Brush, Flower2], "#fecdd3", "#e11d48"],
  ["Jewellery & Accessories", Gem, [Watch, Gem, Glasses], "#fed7aa", "#ea580c"],
  ["Arts & Crafts", Palette, [Paintbrush, Scissors, Palette], "#ddd6fe", "#7c3aed"],
  ["Restaurants & Cafes", UtensilsCrossed, [Coffee, Pizza, Cherry], "#fde68a", "#b45309"],
  ["Sports & Fitness", Dumbbell, [Trophy, Footprints, Dumbbell], "#bbf7d0", "#15803d"],
];

/** A tiny storefront for a business type — drawn in the same style as the phone mock-ups, no photos. */
function CategoryArt({ hero: Hero, icons, bg, accent, name }) {
  return (
    <div className="h-28 rounded-xl overflow-hidden relative shadow-sm group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300" style={{ background: `linear-gradient(150deg, ${bg}, #ffffff 160%)` }} role="img" aria-label={`${name} store`}>
      <div className="absolute inset-x-3 top-3 bottom-0 rounded-t-xl bg-white shadow-md border border-white overflow-hidden transition-transform duration-500 group-hover:scale-[1.04] origin-bottom">
        <div className="flex items-center gap-1 px-1.5 py-1 border-b border-slate-100">
          <i className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} /><i className="h-1.5 flex-1 rounded-full bg-slate-100" /><i className="w-3 h-1.5 rounded-full bg-slate-200" />
        </div>
        <div className="mx-1.5 mt-1.5 rounded-md h-9 px-2 flex items-center justify-between" style={{ background: `linear-gradient(120deg, ${accent}, ${accent}cc)` }}>
          <div className="space-y-1"><i className="block h-1.5 w-11 rounded-full bg-white/90" /><i className="block h-1 w-7 rounded-full bg-white/60" /><i className="block h-2 w-6 rounded bg-white/95" /></div>
          <Hero size={24} className="text-white/95 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110" strokeWidth={1.6} />
        </div>
        <div className="grid grid-cols-3 gap-1 px-1.5 mt-1.5">
          {icons.map((Icon, i) => (
            <div key={i} className="rounded-md p-1 flex flex-col items-center gap-0.5" style={{ background: `${bg}99` }}>
              <Icon size={13} style={{ color: accent }} strokeWidth={1.8} />
              <i className="block h-1 w-5 rounded-full bg-slate-300" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricingSection({ nav }) {
  const { plans, error } = usePlans();
  if (plans && plans.length === 0 && !error) return null;      // nothing to show: no empty section
  return (
    <section id="pricing" className="px-6 py-16 bg-white" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto text-center">
        <Reveal><div className="text-[11px] font-bold tracking-widest text-orange-500">SIMPLE, AFFORDABLE PRICING</div>
          <h2 className="font-display text-3xl font-extrabold text-neutral-900 mt-2">Plans for every stage of your business</h2>
          <p className="text-sm text-slate-500 mt-2">Billed monthly. Change or cancel your plan any time.</p></Reveal>
        {error ? (
          <p className="mt-8 text-sm text-slate-500" data-testid="pricing-error">Plans are unavailable right now. Please try again in a moment.</p>
        ) : (
          <div className="flex flex-wrap justify-center gap-5 mt-10 text-left items-stretch mx-auto">
            {(plans || [null, null, null]).map((p, i) => !p ? (
              <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 h-72 animate-pulse" style={{ flex: "1 1 240px", maxWidth: "calc(33.333% - 14px)" }} aria-hidden />
            ) : (
              <Reveal key={p.id} custom={i} data-testid={`pricing-card-${p.id}`} style={{ flex: "1 1 240px", maxWidth: `calc(${(100 / Math.min(plans.length, 4)).toFixed(3)}% - ${(20 * (Math.min(plans.length, 4) - 1) / Math.min(plans.length, 4)).toFixed(1)}px)` }}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${p.popular ? "border-blue-400 shadow-xl" : "border-slate-200 shadow-sm"}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-orange-500 text-white px-3 py-1 rounded-full">Most Popular</span>}
                <div className="font-display font-bold text-neutral-900">{p.name}</div>
                {p.description && <div className="text-[11px] text-slate-400">{p.description}</div>}
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-4xl font-extrabold text-neutral-900" data-testid={`pricing-price-${p.id}`}>{p.price > 0 ? priceText(p.price) : "Free"}</span>
                  {p.price > 0 && <span className="text-xs text-slate-400 mb-1.5">/month</span>}
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {(p.features || []).map((f) => <li key={f} className="flex gap-2 text-xs text-slate-600"><Check size={13} className="text-green-500 mt-0.5 shrink-0" />{f}</li>)}
                </ul>
                <button onClick={() => nav("/signup")} data-testid={`pricing-select-${p.id}`}
                  className={`mt-5 w-full py-2.5 rounded-lg text-sm font-bold transition-colors ${p.popular ? "shine-btn bg-blue-600 text-white hover:bg-blue-700" : "border border-slate-300 text-slate-700 hover:border-blue-500 hover:text-blue-600"}`}>
                  {p.price > 0 ? "Get Started" : "Start Free"}
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

const priceText = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN");

/** Plans exactly as the super admin manages them (Admin → Plans): name, price, description, features, "popular". */
function usePlans() {
  const [state, setState] = useState({ plans: null, error: false });
  useEffect(() => {
    let live = true;
    api.get("/plans").then(({ data }) => { if (live) setState({ plans: Array.isArray(data) ? data : [], error: false }); })
      .catch(() => { if (live) setState({ plans: [], error: true }); });
    return () => { live = false; };
  }, []);
  return state;
}

/** Live demo stores, one per kind of business. Each is seeded on backend start (backend/seed.py). */
const DEMOS = [
  ["Arogya Medical Store", "Pharmacy & healthcare", "arogya-medical-store", "#0f766e", "medical"],
  ["Kalakriti Handicrafts", "Art & craft", "kalakriti-handicrafts", "#8c2f39", "crafts"],
  ["Greenfield Grocery", "Food & grocery", "greenfield-grocery", "#1b8a5a", "grocery"],
  ["MeatVerse", "Meat, seafood & eggs", "meatverse", "#e11d2e", "meat"],
  ["Apex Gear", "Electronics & sports", "apex-gear", "#1d4ed8", "electronics"],
  ["Divine Drops", "Beauty & cosmetics", "divine-drops", "#65a30d", "beauty"],
  ["Page Turner Books", "Books & stationery", "page-turner", "#3CAA87", "books"],
];

function DemoMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} data-testid="nav-demo-store" className="lp-link flex items-center gap-1 whitespace-nowrap">
        Live Demo <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50" data-testid="demo-menu">
          <div className="w-72 rounded-xl bg-white border border-slate-200 shadow-xl p-2">
            <div className="px-3 pt-2 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Try a working store</div>
            {DEMOS.map(([name, kind, slug, color, key]) => (
              <a key={slug} href={`/store/${slug}`} target="_blank" rel="noopener noreferrer" role="menuitem" onClick={() => setOpen(false)} data-testid={`demo-${key}`}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[13px] font-extrabold shrink-0" style={{ background: color }}>{name[0]}</span>
                <span className="min-w-0"><span className="block text-[13px] font-bold text-slate-800 truncate">{name}</span><span className="block text-[11px] font-medium text-slate-500">{kind}</span></span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const NAV_LINKS = [["Home", "#home"], ["Features", "#features"], ["Themes", "#themes"], ["Pricing", "#pricing"], ["Business Categories", "#categories"], ["Resources", "#support"]];

// Mobile / tablet menu: slides down under the header with every link, the demo stores and both actions.
function MobileMenu({ open, onClose, nav }) {
  const [demos, setDemos] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-white overflow-y-auto" data-testid="mobile-menu">
      <nav className="px-5 py-3 text-[15px] font-semibold text-slate-700">
        {NAV_LINKS.map(([label, href]) => (
          <a key={href} href={href} onClick={onClose} className="block py-3.5 border-b border-slate-100">{label}</a>
        ))}
        <button type="button" onClick={() => setDemos((d) => !d)} aria-expanded={demos} data-testid="mobile-demo-toggle"
          className="w-full flex items-center justify-between py-3.5 border-b border-slate-100">
          Live Demo <ChevronDown size={16} className={`transition-transform ${demos ? "rotate-180" : ""}`} />
        </button>
        {demos && DEMOS.map(([name, kind, slug, color]) => (
          <a key={slug} href={`/store/${slug}`} target="_blank" rel="noopener noreferrer" onClick={onClose} className="flex items-center gap-3 py-2.5 pl-2">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[13px] font-extrabold shrink-0" style={{ background: color }}>{name[0]}</span>
            <span className="min-w-0"><span className="block text-[14px] font-bold text-slate-800 truncate">{name}</span><span className="block text-[11px] font-medium text-slate-500">{kind}</span></span>
          </a>
        ))}
      </nav>
      <div className="px-5 pt-3 pb-8 grid grid-cols-2 gap-3">
        <button onClick={() => { onClose(); nav("/login"); }} data-testid="mobile-login" className="h-11 rounded-lg border border-slate-300 text-[14px] font-semibold text-slate-700">Login</button>
        <button onClick={() => { onClose(); nav("/signup"); }} data-testid="mobile-signup" className="h-11 rounded-lg bg-blue-600 text-white text-[14px] font-bold">Get Started Free</button>
      </div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const carousel = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap";
    document.head.appendChild(l);
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); l.remove(); };
  }, []);

  const slide = (dir) => carousel.current?.scrollBy({ left: dir * 260, behavior: "smooth" });

  return (
    <div className="lp min-h-screen bg-white text-slate-700 overflow-x-hidden font-['Plus_Jakarta_Sans',Inter,sans-serif]">
      {/* Nav */}
      <header className={`fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md transition-shadow ${scrolled ? "shadow-md" : "border-b border-slate-100"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="nav-landing-logo" className="shrink-0"><Brand /></Link>
          <nav className="hidden lg:flex items-center gap-7 text-[13px] font-semibold text-slate-600">
            <a href="#home" className="lp-link text-blue-700">Home</a>
            <a href="#features" className="lp-link">Features</a>
            <a href="#themes" className="lp-link">Themes</a>
            <a href="#pricing" className="lp-link">Pricing</a>
            <a href="#categories" className="lp-link">Business Categories</a>
            <a href="#support" className="lp-link flex items-center gap-1">Resources <ChevronDown size={13} /></a>
            <DemoMenu />
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => nav("/login")} data-testid="nav-btn-signin"
              className="hidden sm:inline-flex items-center h-9 px-5 text-[13px] font-semibold rounded-lg border border-slate-300 text-slate-700 hover:border-blue-600 hover:text-blue-700 transition-colors whitespace-nowrap">Login</button>
            <button onClick={() => nav("/signup")} data-testid="nav-btn-launch-store"
              className="shine-btn inline-flex items-center h-9 px-3.5 sm:px-5 rounded-lg text-[12px] sm:text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-[0_6px_16px_rgba(37,99,235,0.35)] transition-colors whitespace-nowrap">
              Get Started<span className="hidden sm:inline">&nbsp;Free</span>
            </button>
            <button onClick={() => setMenu((m) => !m)} aria-label={menu ? "Close menu" : "Open menu"} aria-expanded={menu} data-testid="nav-mobile-toggle"
              className="lg:hidden w-9 h-9 rounded-lg border border-slate-300 text-slate-700 flex items-center justify-center">
              {menu ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={menu} onClose={() => setMenu(false)} nav={nav} />

      {/* Hero */}
      <section id="home" ref={heroRef} className="relative pt-28 pb-16 px-6 overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-white">
        <motion.div style={{ y: blobY }} className="lp-blob absolute -right-24 top-10 w-[520px] h-[520px] rounded-full bg-blue-200/50 blur-3xl" />
        <div className="lp-blob absolute -left-32 top-64 w-80 h-80 rounded-full bg-orange-100/60 blur-3xl" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="inline-block text-[11px] font-semibold px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
              Launch &bull; Sell &bull; Grow
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
              className="font-display text-5xl lg:text-[64px] font-extrabold tracking-tight leading-[1.05] mt-5 text-neutral-900">
              Your Online Store<br /><span className="text-blue-600">in Minutes.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7 }}
              className="mt-5 text-[15px] text-slate-500 max-w-md leading-relaxed">
              Create a professional e-commerce store, add your products and start selling online. Everything you need &mdash; website, mobile app, payments, delivery, marketing and more &mdash; in one affordable platform.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => nav("/signup")} data-testid="hero-btn-start"
                className="shine-btn lp-pulse group px-6 py-3 rounded-lg font-bold text-sm text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center gap-2">
                Get Started Free <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/store/apex-gear" data-testid="hero-btn-demo"
                className="px-6 py-3 rounded-lg font-semibold text-sm border border-slate-300 text-slate-700 hover:border-orange-500 hover:text-orange-600 transition-colors flex items-center gap-2">
                <Store size={16} className="text-orange-500" /> Book a Free Demo
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
              className="mt-6 flex flex-wrap items-center gap-5 text-xs text-slate-500">
              {["No credit card required", "Easy setup", "24x7 support"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-500" />{t}</span>
              ))}
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.92, x: 40 }} animate={{ opacity: 1, scale: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="relative pt-10 pr-0 lg:pr-24">
            <motion.div animate={{ rotate: [-3, 3, -3] }} transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
              className="absolute left-16 -top-2 font-['Caveat',cursive] text-3xl font-bold text-blue-700 -rotate-6 select-none">
              Your Store is LIVE!
              <svg viewBox="0 0 60 30" className="w-12 h-6 ml-14 text-blue-700"><path d="M2 4 C 20 4, 40 10, 52 26 M52 26 l-10 -2 M52 26 l-2 -10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </motion.div>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }} className="relative">
              <LaptopMock />
              <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.5 }}
                className="absolute -left-4 bottom-4 w-[22%]"><TabletMock /></motion.div>
            </motion.div>
            <div className="hidden lg:flex flex-col gap-2 absolute right-0 top-6 w-44">
              {[[Palette, "Choose a Theme", "or Generate with AI", "#7c3aed"], [Package, "Add Products", "in Minutes", "#2563eb"],
                [CreditCard, "Accept Payments", "", "#f59e0b"], [Store, "Start Selling", "Online", "#16a34a"]].map(([Icon, a, b, c], i) => (
                <motion.div key={a} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + i * 0.15 }}
                  whileHover={{ x: -6, scale: 1.04 }}
                  className="flex items-center gap-2 bg-white rounded-xl shadow-lg border border-slate-100 px-3 py-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${c}1a`, color: c }}><Icon size={14} /></span>
                  <span className="text-[10px] leading-tight font-bold text-slate-700">{a}{b && <><br /><span className="font-medium text-slate-400">{b}</span></>}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Icon row */}
      <section id="features" className="px-6 py-8">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {FEATURES.map(([Icon, label], i) => (
            <Reveal key={label} custom={i} className="group text-center cursor-default">
              <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center text-blue-600 bg-blue-50 group-hover:bg-blue-600 group-hover:text-white group-hover:-translate-y-1.5 group-hover:shadow-lg transition-all duration-300">
                <Icon size={26} strokeWidth={1.6} />
              </div>
              <div className="mt-2 text-[10px] font-semibold text-slate-600 leading-tight px-1">{label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Themes banner */}
      <section id="themes" className="px-6 py-8">
        <Reveal className="max-w-7xl mx-auto rounded-3xl bg-gradient-to-r from-blue-50 to-sky-100/70 border border-blue-100 grid lg:grid-cols-2 gap-6 items-center p-8 lg:p-10 overflow-hidden relative">
          <div>
            <div className="text-[11px] font-bold tracking-widest text-orange-500">BEAUTIFUL, PROFESSIONAL DESIGNS</div>
            <h2 className="font-display text-3xl lg:text-4xl font-extrabold text-neutral-900 leading-tight mt-2">
              Choose from 100+ ready-to-use themes or create a unique design with <span className="text-blue-600">AI.</span>
            </h2>
            <p className="text-sm text-slate-500 mt-3 max-w-md">Get started with professionally designed themes for every business. Or just describe your idea and let our AI builder generate a custom theme for you.</p>
            <button onClick={() => nav("/signup")} className="shine-btn mt-5 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold flex items-center gap-2 transition-colors">
              Explore Themes <ArrowRight size={15} />
            </button>
          </div>
          <div className="relative h-56 lg:h-64">
            <motion.div whileHover={{ scale: 1.03, rotate: -1 }} animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 6 }}
              className="absolute left-0 top-0 w-[68%] rounded-xl bg-white shadow-xl p-2">
              <div className="h-32 rounded-lg bg-gradient-to-br from-amber-100 via-orange-200 to-amber-300 p-3">
                <div className="text-[9px] font-bold text-slate-700">Make your house<br />feel like home</div>
                <div className="mt-1 text-[7px] bg-orange-500 text-white rounded px-1.5 py-0.5 inline-block">Shop Now</div>
              </div>
              <div className="grid grid-cols-4 gap-1 mt-1.5">{[0, 1, 2, 3].map((i) => <div key={i} className="h-8 rounded bg-slate-100" />)}</div>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05, rotate: 1 }} animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 5, delay: 0.4 }}
              className="absolute right-0 bottom-0 w-[44%] rounded-xl bg-white shadow-xl p-2">
              <div className="h-32 rounded-lg bg-gradient-to-br from-rose-200 to-pink-400 p-2 text-[8px] font-bold text-white">Trendy Fashion<br />for You</div>
            </motion.div>
            <Sparkles className="absolute right-2 top-2 text-orange-500 animate-pulse" size={22} />
          </div>
        </Reveal>
      </section>

      {/* Integrations */}
      <section className="px-6 py-14">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal><div className="text-[11px] font-bold tracking-widest text-orange-500">BUILT-IN INTEGRATIONS</div>
            <h2 className="font-display text-3xl font-extrabold text-neutral-900 mt-2">Everything you need to run your store</h2>
            <p className="text-sm text-slate-500 mt-2">Easily connect with leading payment providers, delivery partners and essential tools.</p></Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
            {INTEGRATIONS.map(([n, c, st], i) => (
              <Reveal key={n} custom={i % 6} className="h-16 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center hover:shadow-lg hover:-translate-y-1 hover:border-blue-200 transition-all duration-300">
                <span className={`font-display font-extrabold text-lg ${st}`} style={{ color: c }}>{n}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 py-14">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center"><h2 className="font-display text-4xl font-extrabold text-neutral-900">Live in <span className="text-blue-600">3 simple steps.</span></h2>
            <p className="text-sm text-slate-500 mt-2">Your online store doesn&apos;t need a year of building. Get started today.</p></Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            {[
              ["Sign up and pick a template", ["Sign up for free. No credit card needed.", "Choose a theme or generate with AI", "Add your logo and store details"]],
              ["Add your products", ["Upload product images, prices and descriptions", "Set delivery zones, store hours and payment methods", "Preview your store and mobile app"]],
              ["Start selling online", ["Your store is live on web and mobile (Android & iOS)", "Customers order, pay and track in real time", "Manage orders, inventory and customers from one dashboard"]],
            ].map(([t, items], i) => (
              <Reveal key={t} custom={i} className="relative group">
                <div className="flex items-center gap-3">
                  <motion.span whileInView={{ scale: [0.6, 1.15, 1] }} viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.15, duration: 0.5 }}
                    className="w-8 h-8 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center shadow-md">{i + 1}</motion.span>
                  <h3 className="font-display font-bold text-lg text-neutral-900">{t}</h3>
                </div>
                <ul className="mt-4 space-y-2.5">
                  {items.map((x) => <li key={x} className="flex gap-2 text-[13px] text-slate-500"><Check size={14} className="text-green-500 mt-0.5 shrink-0" />{x}</li>)}
                </ul>
                {i < 2 && <svg viewBox="0 0 60 24" className="hidden md:block absolute -right-10 top-0 w-14 text-blue-400"><path d="M2 20 C 20 0, 40 0, 56 14 M56 14 l-9 -1 M56 14 l-3 -9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile ready */}
      <section className="px-6 py-12 bg-gradient-to-r from-sky-50 to-blue-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
          <Reveal variants={fromLeft} className="flex justify-center items-end gap-3">
            {["home", "product", "orders"].map((v, i) => (
              <motion.div key={v} animate={{ y: [0, i === 1 ? -14 : -8, 0] }} transition={{ repeat: Infinity, duration: 4 + i, ease: "easeInOut" }}
                className={i === 1 ? "w-36" : "w-32"}>
                <PhoneMock variant={v} />
              </motion.div>
            ))}
          </Reveal>
          <Reveal variants={fromRight}>
            <h2 className="font-display text-4xl font-extrabold text-neutral-900 leading-tight"><span className="text-blue-600">100% mobile ready</span><br /><span className="text-blue-600">online stores.</span></h2>
            <p className="text-sm text-slate-500 mt-3 max-w-sm">Your store looks perfect on every device. Give your customers a seamless shopping experience on web and mobile.</p>
            <ul className="mt-5 space-y-2.5">
              {["Beautiful home page", "Dedicated product pages", "Easy search & filters", "Smooth checkout experience"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm font-medium text-slate-700"><CheckCircle2 size={17} className="text-green-500" />{x}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Categories carousel */}
      <section id="categories" className="px-6 py-14">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal><h2 className="font-display text-3xl font-extrabold text-neutral-900">Open a store for <span className="text-blue-600">any business</span></h2>
            <p className="text-sm text-slate-500 mt-2">Sell24x7 is perfect for businesses of all types and sizes.</p></Reveal>
          <div className="relative mt-8">
            <button onClick={() => slide(-1)} aria-label="Previous" className="absolute -left-3 top-1/3 z-10 w-8 h-8 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><ChevronLeft size={16} /></button>
            <div ref={carousel} className="lp-scroll flex gap-4 overflow-x-auto scroll-smooth pb-2 px-1">
              {CATEGORIES.map(([n, hero, icons, bg, accent], i) => (
                <Reveal key={n} custom={i % 4} className="group shrink-0 w-[150px] cursor-pointer" data-testid={`category-card-${i}`}>
                  <CategoryArt name={n} hero={hero} icons={icons} bg={bg} accent={accent} />
                  <div className="text-[11px] font-semibold text-slate-700 mt-2 leading-tight">{n}</div>
                </Reveal>
              ))}
              <Reveal custom={CATEGORIES.length % 4} className="group shrink-0 w-[150px] cursor-pointer" data-testid="category-more" onClick={() => nav("/signup")}>
                <div className="h-28 rounded-xl border-2 border-dashed border-blue-300 bg-gradient-to-br from-blue-50 to-white flex flex-col items-center justify-center gap-1.5 text-blue-600 group-hover:border-blue-500 group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                  <span className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md transition-transform duration-300 group-hover:rotate-90"><Plus size={20} /></span>
                  <span className="text-[11px] font-bold">&amp; many more</span>
                </div>
                <div className="text-[11px] font-semibold text-slate-700 mt-2 leading-tight">Books, Pharmacy, Digital goods &amp; more</div>
              </Reveal>
            </div>
            <button onClick={() => slide(1)} aria-label="Next" className="absolute -right-3 top-1/3 z-10 w-8 h-8 rounded-full bg-white shadow-lg border border-slate-100 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>
      </section>

      {/* Admin dashboard */}
      <section className="px-6 py-16 bg-gradient-to-br from-black via-neutral-950 to-black relative overflow-hidden">
        <div className="absolute -left-20 top-0 w-80 h-80 bg-blue-500/10 blur-3xl rounded-full lp-blob" />
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-center relative">
          <Reveal variants={fromLeft}>
            <div className="text-[11px] font-bold tracking-widest text-orange-500">MANAGE YOUR STORE WITH EASE</div>
            <h2 className="font-display text-4xl font-extrabold text-white leading-tight mt-2">Powerful Admin Dashboard <span className="text-blue-400">for complete control.</span></h2>
            <p className="text-sm text-slate-300 mt-3 max-w-sm">Manage products, orders, customers, inventory, promotions and analytics &mdash; all from a single, easy-to-use dashboard.</p>
            <ul className="mt-5 space-y-2.5">
              {["List products in a breeze", "Receive and process orders seamlessly", "Track sales with real-time analytics", "Manage your team and roles"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-slate-200"><CheckCircle2 size={16} className="text-green-400" />{x}</li>
              ))}
            </ul>
            <button onClick={() => nav("/login")} className="shine-btn mt-6 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-400 text-white text-sm font-bold flex items-center gap-2 transition-colors">
              See Dashboard Demo <ArrowRight size={15} />
            </button>
          </Reveal>
          <Reveal variants={fromRight} className="min-w-0">
            <motion.div whileHover={{ rotateY: -4, rotateX: 2, scale: 1.02 }} style={{ perspective: 1000 }} transition={{ type: "spring", stiffness: 120 }}>
              <DashboardShowcase />
            </motion.div>
          </Reveal>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4 mt-12 text-center relative">
          {[[LayoutDashboard, 124, "", "K", "Orders", null], [Users, 856, "", "", "Customers", null], [Tag, 14500, "₹", "K", "Sales", (x) => `${Math.floor(x / 100)},${String(Math.floor(x % 100)).padStart(2, "0")}`]].map(([Icon, n, p, sfx, l, fmt]) => (
            <div key={l} className="rounded-xl bg-white/5 border border-white/10 py-4">
              <Icon className="mx-auto text-blue-400" size={18} />
              <div className="font-display text-2xl font-extrabold text-white mt-1"><CountUp to={n} prefix={p} suffix={sfx} format={fmt || undefined} /></div>
              <div className="text-[11px] text-slate-400">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <PricingSection nav={nav} />

      {/* Support */}
      <section id="support" className="relative bg-gradient-to-r from-blue-800 to-blue-600 px-6 py-14 overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-blue-400/30 to-transparent" />
        <div className="max-w-6xl mx-auto relative grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
          <Reveal>
            <div className="font-['Caveat',cursive] text-2xl text-orange-300">Customer Support</div>
            <h2 className="font-display text-4xl font-extrabold text-white leading-tight max-w-md">No problem too big,<br />no question too small.</h2>
            <p className="text-sm text-blue-100/80 mt-3 max-w-md">We&apos;re committed to the growth of eCommerce businesses, just as you are. Reach out to us wherever you need.</p>
          </Reveal>
          <div className="flex flex-wrap gap-3 mt-6">
            {[[Mail, "hello@mucept.com", "mailto:hello@mucept.com", "contact-email"],
              [PhoneCall, "+91 7500110071", "tel:+917500110071", "contact-call"],
              [MessageCircle, "Chat on WhatsApp", `https://wa.me/917500110071?text=${encodeURIComponent("Hi, I'd like to know more about Sell24x7.")}`, "contact-whatsapp"]].map(([Icon, t, href, tid], i) => (
              <motion.a key={t} href={href} data-testid={tid} {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                whileHover={{ y: -3 }} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-slate-700 text-xs font-semibold shadow-lg">
                <Icon size={14} className="text-blue-700" />{t}
              </motion.a>
            ))}
          </div>
          </div>
          <Reveal variants={fromRight} className="min-w-0"><SupportArt /></Reveal>
        </div>
      </section>

      <footer className="bg-black py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Brand dark />
          <p className="text-xs text-slate-400">&copy; 2026 Sell24x7 &mdash; Your eCom Street. All rights reserved.</p>
          <a href="https://mucept.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-slate-200" data-testid="footer-powered-by" aria-label="Powered by Mucept">
            Powered by <img src="/mucept-logo.png" alt="Mucept" className="h-6 w-auto" />
          </a>
        </div>
      </footer>
    </div>
  );
}
